import { z } from "zod";
import { queryOptions, type QueryClient, type UseQueryOptions } from "@tanstack/react-query";

import { api, ApiOptions, mBaseURL } from "@/config/api";
import { unwrapApiError } from "@/lib/error";
import { APIResponseSuccessSchema } from "@/schema/api";

type QueryOptions<T> = Omit<UseQueryOptions<T>, "queryKey" | "queryFn">;

const CreatePlaybookRequestSchema = z.object({
  markdown: z.string(),
  section_count: z.number(),
});

const CreatePlaybookResponseSchema = APIResponseSuccessSchema.extend({
  data: z.unknown().optional(),
}).passthrough();

const UpdatePlaybookRequestSchema = z.object({
  moderation_queue_id: z.string(),
  rejection_prompt: z.string(),
});

const UpdatePlaybookResponseSchema = APIResponseSuccessSchema.extend({
  data: z.unknown().optional(),
}).passthrough();

const PlaybookSchema = z
  .object({
    id: z.string().optional(),
    markdown: z.string().optional(),
    section_count: z.number().optional(),
    created_at: z.string().optional(),
    version: z.number().optional(),
    brand_name: z.string().optional()
  })
  .passthrough();

const GetPlaybooksResponseSchema = APIResponseSuccessSchema.extend({
  data: z
    .object({
      playbooks: z.array(PlaybookSchema),
    })
    .optional(),
}).passthrough();

export const PlaybooksApiFactory = {
  Endpoint: mBaseURL + "/api/brand/moderation/playbooks",
  PlaybookFeedbackEndpoint: mBaseURL + "/api/brand/moderation/playbook-feedback",

  Keys: {
    GetPlaybooks: ["brand-moderation", "playbooks", "list"] as const,
  },

  Invalidations: {
    AfterCreatePlaybook: async (queryClient: QueryClient) => {
      await queryClient.invalidateQueries({ queryKey: PlaybooksApiFactory.Keys.GetPlaybooks });
    },
    AfterUpdatePlaybook: async (queryClient: QueryClient) => {
      await queryClient.invalidateQueries({ queryKey: PlaybooksApiFactory.Keys.GetPlaybooks });
    },
  },

  Schemas: {
    Request: {
      CreatePlaybook: CreatePlaybookRequestSchema,
      UpdatePlaybook: UpdatePlaybookRequestSchema,
    },
    Response: {
      CreatePlaybook: CreatePlaybookResponseSchema,
      UpdatePlaybook: UpdatePlaybookResponseSchema,
      GetPlaybooks: GetPlaybooksResponseSchema,
    },
  },

  Apis: {
    GetPlaybooks: async (options?: ApiOptions) => {
      const response = await api(PlaybooksApiFactory.Endpoint, {
        method: "GET",
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(GetPlaybooksResponseSchema.parse);
    },
    CreatePlaybook: async (body: z.infer<typeof CreatePlaybookRequestSchema>, options?: ApiOptions) => {
      const response = await api(PlaybooksApiFactory.Endpoint, {
        method: "POST",
        body: JSON.stringify(body),
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(CreatePlaybookResponseSchema.parse);
    },
    /** POST /api/brand/moderation/playbook-feedback — submit rejection feedback to refresh the playbook */
    UpdatePlaybook: async (body: z.infer<typeof UpdatePlaybookRequestSchema>, options?: ApiOptions) => {
      const response = await api(PlaybooksApiFactory.PlaybookFeedbackEndpoint, {
        method: "POST",
        body: JSON.stringify(body),
        ...(options || {}),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: "Oops, something went wrong! Please try again." }));
        throw new Error(unwrapApiError(error));
      }

      return response.json().then(UpdatePlaybookResponseSchema.parse);
    },
  },

  Queries: {
    GetPlaybooks: (options?: QueryOptions<z.infer<typeof GetPlaybooksResponseSchema>>) =>
      queryOptions({
        queryKey: PlaybooksApiFactory.Keys.GetPlaybooks,
        queryFn: () => PlaybooksApiFactory.Apis.GetPlaybooks(),
        ...options,
      }),
  },
};

export type CreatePlaybookRequest = z.infer<typeof CreatePlaybookRequestSchema>;
export type CreatePlaybookResponse = z.infer<typeof CreatePlaybookResponseSchema>;
export type UpdatePlaybookRequest = z.infer<typeof UpdatePlaybookRequestSchema>;
export type UpdatePlaybookResponse = z.infer<typeof UpdatePlaybookResponseSchema>;
export type Playbook = z.infer<typeof PlaybookSchema>;
export type GetPlaybooksResponse = z.infer<typeof GetPlaybooksResponseSchema>;
